// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { CROP_STATUS } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useGating } from '../../context/UserContext';
import { MenuItem } from '../../lib/base';
import { ArchiveIcon, EditIcon, FileCopyIcon, UnarchiveIcon } from '../../lib/icons';
import { CROP_NAMES_QUERY } from '../../queries/CropNamesQuery';
import { ARCHIVED_CROPS_QUERY, CROPS_QUERY } from '../../queries/CropsQueries';
import type { ArchiveCropMutation, ArchiveCropMutationVariables } from '../../types/__generated__/graphql';
import { getNumericID } from '../../utils';
import { Options } from '../common/dropdown';
import AddCropDialog from './AddCropDialog';
import EditCropBufferPercentDialog from './EditCropBufferPercentDialog';
import EditCropGrowTimeStepsDialog from './EditCropGrowTimeStepsDialog';
import EditCropHarvestBufferTimeDialog from './EditCropHarvestBufferTimeDialog';
import EditCropMaxHoldTimeDialog from './EditCropMaxHoldTimeDialog';
import EditCropNameDialog from './EditCropNameDialog';
import EditCropNotesDialog from './EditCropNotesDialog';
import EditCropSizesDialog from './EditCropSizesDialog';
import EditCropSowDensityDialog from './EditCropSowDensityDialog';
import EditCropYieldDialog from './EditCropYieldDialog';

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isArchived: boolean;
  readonly useMoreIcon?: boolean;
}

const ARCHIVE_MUTATION = gql`
  mutation ArchiveCrop($id: Int!, $new_status: String!) {
    archiveCrop(input: { crop_id: $id, new_status: $new_status }) {
      updated_crop {
        id
        status
        updated_at
      }
    }
  }
`;

export default function CropOptions({ cropId, cropName, isArchived, useMoreIcon }: Props) {
  const { canUseBasic } = useGating();
  const [cloneOpen, setCloneOpen] = React.useState(false);
  const [editNameOpen, setEditNameOpen] = React.useState(false);
  const [editNotesOpen, setEditNotesOpen] = React.useState(false);
  const [editYieldOpen, setEditYieldOpen] = React.useState(false);
  const [editSowDensityOpen, setEditSowDensityOpen] = React.useState(false);
  const [editMaxHoldTimeOpen, setEditMaxHoldTimeOpen] = React.useState(false);
  const [editHarvestBufferTimeOpen, setEditHarvestBufferTimeOpen] = React.useState(false);
  const [editGrowTimeOpen, setEditGrowTimeOpen] = React.useState(false);
  const [editSizesOpen, setEditSizesOpen] = React.useState(false);
  const [editBufferPercentOpen, setEditBufferPercentOpen] = React.useState(false);
  const [optionsClose, setOptionsClose] = React.useState(false);
  const [archiveCrop] = useMutation<ArchiveCropMutation, ArchiveCropMutationVariables>(ARCHIVE_MUTATION);

  React.useEffect(() => {
    setOptionsClose(false);
  });

  const editName = (
    <MenuItem
      key="edit-crop-name"
      onClick={() => {
        setEditNameOpen(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <EditIcon />
      Edit Name
    </MenuItem>
  );
  const editNotes = (
    <MenuItem
      key="edit-crop-notes"
      onClick={() => {
        setEditNotesOpen(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <EditIcon />
      Edit Notes
    </MenuItem>
  );
  const editYield = (
    <MenuItem
      key="edit-crop-yield"
      onClick={() => {
        setEditYieldOpen(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <EditIcon />
      Edit Yield
    </MenuItem>
  );
  const editSowDensity = (
    <MenuItem
      key="edit-crop-sow-density"
      onClick={() => {
        setEditSowDensityOpen(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <EditIcon />
      Edit Sow Density
    </MenuItem>
  );
  const editMaxHoldTime = (
    <MenuItem
      key="edit-crop-shelf-life"
      onClick={() => {
        setEditMaxHoldTimeOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Shelf Life
    </MenuItem>
  );
  const editHarvestBufferTime = (
    <MenuItem
      key="edit-crop-harvest-buffer-time"
      onClick={() => {
        setEditHarvestBufferTimeOpen(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <EditIcon />
      Edit Harvest Buffer Time
    </MenuItem>
  );
  const editGrowTime = (
    <MenuItem
      key="edit-crop-grow-time"
      onClick={() => {
        setEditGrowTimeOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Grow Time and Steps
    </MenuItem>
  );
  const editSizes = (
    <MenuItem
      key="edit-crop-sizes"
      onClick={() => {
        setEditSizesOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Sizes
    </MenuItem>
  );
  const editBufferPercent = (
    <MenuItem
      key="edit-crop-safety-margin"
      onClick={() => {
        setEditBufferPercentOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <EditIcon />
      Edit Safety Margin
    </MenuItem>
  );
  const archive = (
    <MenuItem
      key="archive-crop"
      onClick={() => {
        void archiveCrop({
          refetchQueries: [{ query: ARCHIVED_CROPS_QUERY }],
          variables: { id: getNumericID(cropId), new_status: CROP_STATUS.ARCHIVED },
          onCompleted: () => {
            enqueueSnackbar('Crop archived', { variant: 'success' });
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
        setOptionsClose(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <ArchiveIcon />
      Archive
    </MenuItem>
  );
  const unarchive = (
    <MenuItem
      key="unarchive-crop"
      onClick={() => {
        void archiveCrop({
          refetchQueries: [{ query: CROPS_QUERY }, { query: CROP_NAMES_QUERY }, { query: ARCHIVED_CROPS_QUERY }],
          variables: { id: getNumericID(cropId), new_status: CROP_STATUS.ACTIVE },
          onCompleted: () => {
            enqueueSnackbar('Crop unarchived', { variant: 'success' });
          },
          onError: (errorIn) => {
            enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
          },
        });
        setOptionsClose(true);
      }}
      disabled={!canUseBasic}
      disableRipple
    >
      <UnarchiveIcon />
      Unarchive
    </MenuItem>
  );
  const clone = (
    <MenuItem
      key="clone-crop"
      onClick={() => {
        setCloneOpen(true);
      }}
      disableRipple
      disabled={!canUseBasic}
    >
      <FileCopyIcon />
      Clone
    </MenuItem>
  );

  const optionItems = [
    editYield,
    editBufferPercent,
    editGrowTime,
    editSowDensity,
    editHarvestBufferTime,
    editMaxHoldTime,
    editSizes,
    editNotes,
    editName,
    isArchived ? unarchive : archive,
    clone,
  ];

  return (
    <>
      <Options useMoreIcon={useMoreIcon} close={optionsClose} options={optionItems} />
      {cloneOpen && (
        <AddCropDialog
          isOpen={cloneOpen}
          onClose={() => {
            setCloneOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setCloneOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editNameOpen && (
        <EditCropNameDialog
          cropName={cropName}
          isOpen={editNameOpen}
          onClose={() => {
            setEditNameOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditNameOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editYieldOpen && (
        <EditCropYieldDialog
          cropName={cropName}
          isOpen={editYieldOpen}
          onClose={() => {
            setEditYieldOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditYieldOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editBufferPercentOpen && (
        <EditCropBufferPercentDialog
          cropName={cropName}
          isOpen={editBufferPercentOpen}
          onClose={() => {
            setEditBufferPercentOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditBufferPercentOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editGrowTimeOpen && (
        <EditCropGrowTimeStepsDialog
          cropName={cropName}
          isOpen={editGrowTimeOpen}
          onClose={() => {
            setEditGrowTimeOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditGrowTimeOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editHarvestBufferTimeOpen && (
        <EditCropHarvestBufferTimeDialog
          cropName={cropName}
          isOpen={editHarvestBufferTimeOpen}
          onClose={() => {
            setEditHarvestBufferTimeOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditHarvestBufferTimeOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editSowDensityOpen && (
        <EditCropSowDensityDialog
          cropName={cropName}
          isOpen={editSowDensityOpen}
          onClose={() => {
            setEditSowDensityOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditSowDensityOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editMaxHoldTimeOpen && (
        <EditCropMaxHoldTimeDialog
          cropName={cropName}
          isOpen={editMaxHoldTimeOpen}
          onClose={() => {
            setEditMaxHoldTimeOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditMaxHoldTimeOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editSizesOpen && (
        <EditCropSizesDialog
          cropName={cropName}
          isOpen={editSizesOpen}
          onClose={() => {
            setEditSizesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditSizesOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editBufferPercentOpen && (
        <EditCropBufferPercentDialog
          cropName={cropName}
          isOpen={editBufferPercentOpen}
          onClose={() => {
            setEditBufferPercentOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditBufferPercentOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
      {editNotesOpen && (
        <EditCropNotesDialog
          cropName={cropName}
          isOpen={editNotesOpen}
          onClose={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          onSuccess={() => {
            setEditNotesOpen(false);
            setOptionsClose(true);
          }}
          cropId={cropId}
        />
      )}
    </>
  );
}
